import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DOMPurify from 'dompurify';
import { 
  Sparkles, 
  X, 
  Send, 
  Phone, 
  MessageCircle,
  AlertTriangle,
  Bot
} from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Quick reply buttons - bilingual
const QUICK_REPLIES = {
  he: [
    { id: 'scar-types', text: 'סוגי צלקות' },
    { id: 'treatment-time', text: 'כמה זמן טיפול?' },
    { id: 'pain', text: 'האם זה כואב?' },
    { id: 'booking', text: 'לתיאום פגישה' }
  ],
  en: [
    { id: 'scar-types', text: 'Types of scars' },
    { id: 'treatment-time', text: 'Treatment duration?' },
    { id: 'pain', text: 'Is it painful?' },
    { id: 'booking', text: 'Book appointment' }
  ]
};

// Escalation keywords - bilingual
const ESCALATION_KEYWORDS = {
  he: ['כואב מאוד', 'דלקת', 'מוגלה', 'מדמם', 'חרום', 'דחוף', 'לא רגיל'],
  en: ['very painful', 'infection', 'pus', 'bleeding', 'emergency', 'urgent', 'abnormal']
};

// UI Text - bilingual
const UI_TEXT = {
  he: {
    toggleLabel: 'יועצת AI — שאלי אותי',
    headerTitle: 'יועצת AI של מזל',
    headerSubtitle: 'זמינה 24/7',
    closeChat: 'סגירת צ\'אט',
    disclaimer: 'המידע שמסופק כאן הוא לצרכי מידע כללי בלבד. אינו מהווה תחליף לייעוץ, אבחון או המלצת טיפול על ידי מומחה מנוסה.',
    inputPlaceholder: 'כתבי הודעה...',
    sendMessage: 'שליחת הודעה',
    footer: 'נשלח על ידי מזל AI ✨',
    consentTitle: 'לפני שנתחיל...',
    consentText: 'אני מבין/ה שהיועצת AI מספקת מידע כללי בלבד ואינה תחליף לייעוץ מקצועי.',
    consentButton: 'הבנתי, המשך',
    initialMessage: `שלום! אני יועצת ה-AI של מזל 👋

אני יכולה לענות על שאלות כלליות על טיפולי צלקות.

⚠️ חשוב לדעת: המידע שלי הוא כללי בלבד ואינו תחליף לאבחון ועצה של מזל — מומחית עם 40 שנות ניסיון.

במה אוכל לעזור?`,
    errorMessage: 'מצטערת, אירעה שגיאה. אנא נסו שוב או צרו קשר ישירות עם מזל.',
    escalationMessage: 'נשמע שמדובר במשהו שדורש תשומת לב מיידית. אני ממליצה לפנות ישירות למזל עכשיו:',
    escalationEmergency: 'אם מדובר בדחיפות רפואית — פנה/י לחדר מיון.',
    callButton: 'התקשרי',
    clickHere: 'לחצי כאן'
  },
  en: {
    toggleLabel: 'AI Assistant — Ask me',
    headerTitle: 'Mazal\'s AI Assistant',
    headerSubtitle: 'Available 24/7',
    closeChat: 'Close chat',
    disclaimer: 'The information provided here is for general information purposes only. It is not a substitute for advice, diagnosis or treatment recommendation by an experienced specialist.',
    inputPlaceholder: 'Write a message...',
    sendMessage: 'Send message',
    footer: 'Powered by Mazal AI ✨',
    consentTitle: 'Before we start...',
    consentText: 'I understand that the AI assistant provides general information only and is not a substitute for professional advice.',
    consentButton: 'I understand, continue',
    initialMessage: `Hello! I'm Mazal's AI assistant 👋

I can answer general questions about scar treatments.

⚠️ Important: My information is general only and is not a substitute for diagnosis and advice from Mazal — a specialist with 40 years of experience.

How can I help you?`,
    errorMessage: 'Sorry, an error occurred. Please try again or contact Mazal directly.',
    escalationMessage: 'This sounds like something that requires immediate attention. I recommend contacting Mazal directly now:',
    escalationEmergency: 'If this is a medical emergency — go to the emergency room.',
    callButton: 'Call',
    clickHere: 'click here'
  }
};

// Generate session ID
const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export const AIChatWidget = () => {
  const { language } = useLanguage();
  const t = UI_TEXT[language] || UI_TEXT.he;
  const quickReplies = QUICK_REPLIES[language] || QUICK_REPLIES.he;
  const escalationKeywords = ESCALATION_KEYWORDS[language] || ESCALATION_KEYWORDS.he;
  
  const [isOpen, setIsOpen] = useState(false);
  const [hasConsented, setHasConsented] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [showEscalation, setShowEscalation] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize session
  useEffect(() => {
    const consent = sessionStorage.getItem('aiChatConsent');
    if (consent === 'true') {
      setHasConsented(true);
    }
    setSessionId(generateSessionId());
  }, []);

  // Reset messages when language changes (intentionally only depends on language)
  useEffect(() => {
    if (isOpen && hasConsented) {
      setMessages([{
        role: 'assistant',
        content: t.initialMessage,
        timestamp: new Date().toLocaleTimeString(language === 'he' ? 'he-IL' : 'en-US', { hour: '2-digit', minute: '2-digit' })
      }]);
    }
  }, [language]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && hasConsented) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, hasConsented]);

  // Add initial message when chat opens with consent
  useEffect(() => {
    if (isOpen && hasConsented && messages.length === 0) {
      const initialMessage = {
        role: 'assistant',
        content: t.initialMessage,
        timestamp: new Date().toLocaleTimeString(language === 'he' ? 'he-IL' : 'en-US', { hour: '2-digit', minute: '2-digit' })
      };
      setMessages([initialMessage]);
    }
  }, [isOpen, hasConsented, messages.length, t.initialMessage, language]);

  const handleOpen = () => {
    if (hasConsented) {
      setIsOpen(true);
    } else {
      setShowConsent(true);
    }
  };

  const handleConsent = () => {
    sessionStorage.setItem('aiChatConsent', 'true');
    setHasConsented(true);
    setShowConsent(false);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setShowConsent(false);
  };

  const checkForEscalation = (text) => {
    const lowerText = text.toLowerCase();
    return escalationKeywords.some(keyword => lowerText.includes(keyword.toLowerCase()));
  };

  const handleSendMessage = async (text = inputValue) => {
    if (!text.trim()) return;

    const userMessage = {
      role: 'user',
      content: text,
      timestamp: new Date().toLocaleTimeString(language === 'he' ? 'he-IL' : 'en-US', { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    if (checkForEscalation(text)) {
      setShowEscalation(true);
    }

    try {
      const response = await axios.post(`${API}/chat`, {
        message: text,
        session_id: sessionId,
        language: language,
        history: messages.map(m => ({ role: m.role, content: m.content }))
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toLocaleTimeString(language === 'he' ? 'he-IL' : 'en-US', { hour: '2-digit', minute: '2-digit' })
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      if (response.data.is_escalation) {
        setShowEscalation(true);
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Chat error:', error);
      const errorMessage = {
        role: 'assistant',
        content: t.errorMessage,
        timestamp: new Date().toLocaleTimeString(language === 'he' ? 'he-IL' : 'en-US', { hour: '2-digit', minute: '2-digit' })
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleQuickReply = (reply) => {
    if (reply.id === 'booking') {
      handleClose();
      setTimeout(() => {
        const contactSection = document.getElementById('contact');
        if (contactSection) {
          contactSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      handleSendMessage(reply.text);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const processMessageContent = (content) => {
    const clickText = t.clickHere;
    const regex = new RegExp(`\\[${clickText}\\]`, 'g');
    const withLink = content.replace(
      regex,
      `<a href="#contact" class="text-[#C4956A] underline hover:text-[#b38559] font-semibold" data-scroll-contact="true">${clickText}</a>`
    );
    // Sanitize HTML to prevent XSS — allow only safe anchor tag with specific attrs
    return DOMPurify.sanitize(withLink, {
      ALLOWED_TAGS: ['a', 'strong', 'em', 'br', 'p', 'ul', 'ol', 'li'],
      ALLOWED_ATTR: ['href', 'class', 'data-scroll-contact'],
      ALLOWED_URI_REGEXP: /^(?:#|https?:|mailto:|tel:)/i
    });
  };

  const direction = language === 'he' ? 'rtl' : 'ltr';

  return (
    <>
      {/* Chat Toggle Button */}
      <button
        onClick={handleOpen}
        className="fixed bottom-24 md:bottom-8 left-6 z-[9999] group"
        data-testid="ai-chat-toggle"
        aria-label={t.toggleLabel}
      >
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-[#C4956A] animate-ping opacity-25" />
          <div className="relative w-16 h-16 rounded-full bg-[#C4956A] text-white flex items-center justify-center shadow-lg hover:scale-110 hover:shadow-xl transition-all">
            <Sparkles className="w-7 h-7" />
          </div>
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-[#2C2C2C] text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            {t.toggleLabel}
          </div>
        </div>
      </button>

      {/* Consent Popup */}
      <AnimatePresence>
        {showConsent && (
          <div className="fixed inset-0 z-[10001] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={handleClose}>
            <motion.div
              className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              dir={direction}
            >
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#C4956A]/10 flex items-center justify-center mx-auto mb-4">
                  <Bot className="w-8 h-8 text-[#C4956A]" />
                </div>
                <h3 className="font-['Heebo'] text-xl font-bold text-[#2C2C2C] mb-3">
                  {t.consentTitle}
                </h3>
                <p className="text-[#2C2C2C]/70 mb-6 leading-relaxed">
                  {t.consentText}
                </p>
                <Button
                  onClick={handleConsent}
                  className="w-full rounded-full py-6 bg-[#C4956A] hover:bg-[#b38559] text-white text-lg"
                  data-testid="ai-chat-consent-btn"
                >
                  {t.consentButton}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && hasConsented && (
          <motion.div
            className="fixed bottom-24 md:bottom-8 left-6 z-[10000] w-[380px] max-w-[calc(100vw-48px)] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            style={{ height: '520px' }}
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            dir={direction}
            data-testid="ai-chat-window"
          >
            {/* Header */}
            <div className="bg-[#C4956A] text-white p-4 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-['Heebo'] font-bold">{t.headerTitle}</h3>
                  <p className="text-white/80 text-xs">{t.headerSubtitle}</p>
                </div>
              </div>
              <button 
                onClick={handleClose}
                className="hover:bg-white/20 rounded-full p-2 transition-colors"
                aria-label={t.closeChat}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Disclaimer Banner */}
            <div className="bg-[#FEF3E7] border-b border-[#F59E0B]/30 px-4 py-2 flex-shrink-0">
              <div className="flex items-start gap-2 text-xs text-[#92400E]">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <p>{t.disclaimer}</p>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#FAF7F2]" onClick={(e) => {
              // Delegated handler for sanitized AI message anchors
              const target = e.target.closest('[data-scroll-contact="true"]');
              if (target) {
                e.preventDefault();
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
              }
            }}>
              {messages.map((message, index) => (
                <div
                  key={`msg-${message.timestamp}-${index}`}
                  className={`flex ${message.role === 'user' ? (direction === 'rtl' ? 'justify-start' : 'justify-end') : (direction === 'rtl' ? 'justify-end' : 'justify-start')}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                      message.role === 'user'
                        ? 'bg-[#C4956A] text-white rounded-br-md'
                        : 'bg-white text-[#2C2C2C] shadow-sm rounded-bl-md'
                    }`}
                  >
                    {message.role === 'assistant' ? (
                      <div
                        className="text-sm leading-relaxed whitespace-pre-wrap"
                        dangerouslySetInnerHTML={{ __html: processMessageContent(message.content) }}
                      />
                    ) : (
                      // User messages rendered as plain text — prevents XSS from crafted input
                      <div className="text-sm leading-relaxed whitespace-pre-wrap">
                        {message.content}
                      </div>
                    )}
                    <p className={`text-xs mt-2 ${
                      message.role === 'user' ? 'text-white/60' : 'text-[#2C2C2C]/40'
                    }`}>
                      {message.timestamp}
                    </p>
                  </div>
                </div>
              ))}

              {/* Typing Indicator */}
              {isTyping && (
                <div className={`flex ${direction === 'rtl' ? 'justify-end' : 'justify-start'}`}>
                  <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-[#C4956A] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-[#C4956A] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-[#C4956A] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}

              {/* Quick Replies */}
              {messages.length === 1 && !isTyping && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {quickReplies.map((reply) => (
                    <button
                      key={reply.id}
                      onClick={() => handleQuickReply(reply)}
                      className="px-3 py-1.5 text-sm bg-white border border-[#C4956A] text-[#C4956A] rounded-full hover:bg-[#C4956A] hover:text-white transition-colors"
                      data-testid={`quick-reply-${reply.id}`}
                    >
                      {reply.text}
                    </button>
                  ))}
                </div>
              )}

              {/* Escalation Card */}
              {showEscalation && (
                <div className="bg-[#FEE2E2] border border-[#EF4444]/30 rounded-xl p-4">
                  <p className="text-[#991B1B] text-sm mb-3">
                    {t.escalationMessage}
                  </p>
                  <div className="flex gap-2">
                    <a href="tel:052-3824500" className="flex-1">
                      <Button className="w-full rounded-full bg-[#C4956A] hover:bg-[#b38559] text-white text-sm py-2">
                        <Phone className={`w-4 h-4 ${direction === 'rtl' ? 'ml-1' : 'mr-1'}`} />
                        {t.callButton}
                      </Button>
                    </a>
                    <a href="https://wa.me/972523824500" target="_blank" rel="noopener noreferrer" className="flex-1">
                      <Button className="w-full rounded-full bg-[#25D366] hover:bg-[#128C7E] text-white text-sm py-2">
                        <MessageCircle className={`w-4 h-4 ${direction === 'rtl' ? 'ml-1' : 'mr-1'}`} />
                        WhatsApp
                      </Button>
                    </a>
                  </div>
                  <p className="text-[#991B1B] text-xs mt-2 text-center">
                    {t.escalationEmergency}
                  </p>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-3 border-t border-[#E5E0DA] bg-white flex-shrink-0">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={t.inputPlaceholder}
                  className="flex-1 px-4 py-2 rounded-full border border-[#E5E0DA] focus:border-[#C4956A] focus:ring-2 focus:ring-[#C4956A]/20 outline-none text-sm"
                  disabled={isTyping}
                  data-testid="ai-chat-input"
                />
                <button
                  onClick={() => handleSendMessage()}
                  disabled={!inputValue.trim() || isTyping}
                  className="w-10 h-10 rounded-full bg-[#C4956A] text-white flex items-center justify-center hover:bg-[#b38559] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  aria-label={t.sendMessage}
                  data-testid="ai-chat-send"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="px-3 py-2 border-t border-[#E5E0DA] bg-[#FAF7F2] flex-shrink-0">
              <p className="text-center text-xs text-[#2C2C2C]/40">
                {t.footer}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIChatWidget;
